/**
 * The language contract. **Shared by every app**, present and future.
 *
 * It carries no DOM and no React, for the same reason `config/siteLinks.ts` carries none:
 * `app_mobile` will want the locales and the resolution order, and React Native has neither
 * `document` nor `localStorage`. Everything that touches a browser lives in `detect.ts` and
 * `LocaleProvider.tsx`, which take their inputs as arguments so the rules stay testable and
 * portable.
 */

/** The two languages the platform speaks. Mongolian first in intent, English first in fact. */
export type Locale = 'en' | 'mn'

export const LOCALES = ['en', 'mn'] as const satisfies readonly Locale[]

export const DEFAULT_LOCALE: Locale = 'en'

export interface LocaleInfo {
  code: Locale
  /** In English, for a chooser somebody is using because they cannot read the other one. */
  englishName: string
  /** In the language itself, which is what a native reader looks for. */
  nativeName: string
  /**
   * The BCP 47 tag, for `<html lang>` and for **every `Intl.*` call**.
   *
   * Separate from `code` on purpose. Ours happen to coincide today, and the platform this
   * pattern comes from had short codes like `sp` and `ch` that are not valid Intl locales —
   * `Intl.NumberFormat('sp')` does not throw, it silently formats in the default locale, so
   * a currency renders in the wrong grouping and nobody sees a stack trace. Keeping the two
   * apart means that mistake cannot be made here later.
   */
  intlTag: string
  /** `ltr` for both of ours; the field exists so the provider can set `dir` unconditionally. */
  dir: 'ltr' | 'rtl'
}

/**
 * Both names are shown together in the selector, deliberately.
 *
 * A chooser labelled only in the language you cannot read is a chooser you cannot use, and
 * one labelled only in English is one a Mongolian reader has to translate in their head.
 */
export const LOCALE_INFO: Record<Locale, LocaleInfo> = {
  en: { code: 'en', englishName: 'English', nativeName: 'English', intlTag: 'en-GB', dir: 'ltr' },
  mn: { code: 'mn', englishName: 'Mongolian', nativeName: 'Монгол', intlTag: 'mn-MN', dir: 'ltr' },
}

/**
 * A message catalogue: flat keys to strings.
 *
 * Flat and not nested, because a nested catalogue is a catalogue where two people put the
 * same string in two places. The keys are namespaced by hand — `nav.directory`,
 * `board.empty` — which reads the same and greps better.
 *
 * `Record<string, string>` rather than a generic, because five apps have five different
 * sets of strings and the design system must not know any of them. Each app types its own
 * keys off its English catalogue — see `app_partners/src/i18n/`.
 */
export type Catalogue = Readonly<Record<string, string>>

/** What an app hands the provider: one catalogue per locale. */
export type Catalogues = Readonly<Record<Locale, Catalogue>>

/** A country that means a locale, for the IP step. ISO 3166-1 alpha-2. */
export const LOCALE_BY_COUNTRY: Readonly<Record<string, Locale>> = {
  MN: 'mn',
}

/** True for a value that is one of the locales we speak. */
export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
